import * as DialogPrimitive from "@radix-ui/react-dialog";
import * as TabsPrimitive from "@radix-ui/react-tabs";
import { AlertTriangle, CloudOff, X } from "lucide-react";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";

import { ApiError } from "@/shared/api/client";
import { Button, cn } from "@/shared/ui/primitives";

export function useDocumentTitle(title: string) {
  useEffect(() => {
    document.title = title ? `${title} · Person Learn` : "Person Learn — Your learning. Your journey.";
  }, [title]);
}

export function PageHeader({ title, subtitle, eyebrow, actions }:
  { title: string; subtitle?: ReactNode; eyebrow?: ReactNode; actions?: ReactNode }) {
  useDocumentTitle(title);
  return (
    <header className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
      <div className="min-w-0">
        {eyebrow && <div className="mb-1 text-[13px] font-medium text-ink-3">{eyebrow}</div>}
        <h1 className="font-title text-[30px] leading-tight font-semibold text-ink sm:text-[34px]">{title}</h1>
        {subtitle && <p className="mt-2 max-w-2xl text-[15px] text-ink-2">{subtitle}</p>}
      </div>
      {actions && <div className="flex shrink-0 flex-wrap gap-2">{actions}</div>}
    </header>
  );
}

export function SectionTitle({ children, action, id }: { children: ReactNode; action?: ReactNode; id?: string }) {
  return (
    <div className="mb-3 flex items-center justify-between gap-4">
      <h2 id={id} className="text-[17px] font-semibold text-ink">{children}</h2>
      {action}
    </div>
  );
}

export function EmptyState({ icon, title, children, action }:
  { icon?: ReactNode; title: string; children?: ReactNode; action?: ReactNode }) {
  return (
    <div className="flex flex-col items-center rounded-2xl border border-dashed border-line-strong bg-surface-2 px-6 py-12 text-center">
      {icon && <div className="mb-4 rounded-2xl bg-accent-soft p-3 text-accent-text">{icon}</div>}
      <h3 className="text-[17px] font-semibold text-ink">{title}</h3>
      {children && <div className="mt-2 max-w-md text-[14px] text-ink-2">{children}</div>}
      {action && <div className="mt-6">{action}</div>}
    </div>
  );
}

export function ErrorState({ error, onRetry, title = "We couldn't load this" }:
  { error: unknown; onRetry?: () => void; title?: string }) {
  const offline = error instanceof ApiError && error.status === 0;
  const notFound = error instanceof ApiError && error.status === 404;
  return (
    <div role="alert" className="rounded-2xl border border-line bg-surface p-6">
      <div className="flex items-start gap-3">
        <span className="rounded-xl bg-danger-soft p-2 text-danger">
          {offline ? <CloudOff className="size-5" aria-hidden /> : <AlertTriangle className="size-5" aria-hidden />}
        </span>
        <div className="min-w-0">
          <h3 className="font-semibold text-ink">{notFound ? "Not found" : title}</h3>
          <p className="mt-1 text-[14px] text-ink-2">
            {error instanceof ApiError ? error.message : "Something unexpected happened."}
          </p>
          {onRetry && !notFound && (
            <Button className="mt-4" variant="secondary" size="sm" onClick={onRetry}>Try again</Button>
          )}
        </div>
      </div>
    </div>
  );
}

export function InlineError({ error }: { error: unknown }) {
  if (!error) return null;
  return (
    <p role="alert" className="rounded-xl bg-danger-soft px-3 py-2 text-[14px] text-danger">
      {error instanceof ApiError ? error.message : String(error)}
    </p>
  );
}

export function Dialog({ open, onOpenChange, title, description, children, footer, wide }:
  { open: boolean; onOpenChange: (open: boolean) => void; title: string; description?: ReactNode;
    children?: ReactNode; footer?: ReactNode; wide?: boolean }) {
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-40 bg-black/30 backdrop-blur-[2px]" />
        <DialogPrimitive.Content
          className={cn("fixed left-1/2 top-1/2 z-50 max-h-[88vh] w-[calc(100vw-32px)] -translate-x-1/2 -translate-y-1/2",
            "overflow-y-auto rounded-2xl border border-line bg-surface p-6 shadow-pop focus:outline-none",
            wide ? "max-w-2xl" : "max-w-lg")}>
          <div className="mb-4 flex items-start justify-between gap-4">
            <div>
              <DialogPrimitive.Title className="text-[18px] font-semibold text-ink">{title}</DialogPrimitive.Title>
              {description ? (
                <DialogPrimitive.Description className="mt-1 text-[14px] text-ink-2">{description}</DialogPrimitive.Description>
              ) : (
                <DialogPrimitive.Description className="sr-only">{title}</DialogPrimitive.Description>
              )}
            </div>
            <DialogPrimitive.Close className="rounded-lg p-1.5 text-ink-3 hover:bg-sunken hover:text-ink" aria-label="Close">
              <X className="size-5" aria-hidden />
            </DialogPrimitive.Close>
          </div>
          {children}
          {footer && <div className="mt-6 flex flex-wrap justify-end gap-2">{footer}</div>}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

export function Tabs({ value, onValueChange, tabs, label }:
  { value: string; onValueChange: (value: string) => void; tabs: { value: string; label: string; content: ReactNode }[];
    label: string }) {
  return (
    <TabsPrimitive.Root value={value} onValueChange={onValueChange}>
      <TabsPrimitive.List aria-label={label}
        className="mb-6 flex gap-1 overflow-x-auto rounded-xl bg-sunken p-1 [scrollbar-width:none]">
        {tabs.map((tab) => (
          <TabsPrimitive.Trigger key={tab.value} value={tab.value}
            className="h-9 shrink-0 rounded-lg px-4 text-[14px] font-medium text-ink-2 transition-colors hover:text-ink data-[state=active]:bg-surface data-[state=active]:text-ink data-[state=active]:shadow-card">
            {tab.label}
          </TabsPrimitive.Trigger>
        ))}
      </TabsPrimitive.List>
      {tabs.map((tab) => (
        <TabsPrimitive.Content key={tab.value} value={tab.value} className="focus:outline-none">{tab.content}</TabsPrimitive.Content>
      ))}
    </TabsPrimitive.Root>
  );
}

export function Segmented<T extends string>({ value, onChange, options, label }:
  { value: T; onChange: (value: T) => void; options: { value: T; label: string }[]; label: string }) {
  return (
    <div role="radiogroup" aria-label={label} className="inline-flex rounded-xl bg-sunken p-1">
      {options.map((option) => (
        <button key={option.value} type="button" role="radio" aria-checked={value === option.value}
          onClick={() => onChange(option.value)}
          className={cn("h-8 rounded-lg px-3 text-[13px] font-medium transition-colors",
            value === option.value ? "bg-surface text-ink shadow-card" : "text-ink-2 hover:text-ink")}>
          {option.label}
        </button>
      ))}
    </div>
  );
}

// --- toasts -------------------------------------------------------------------------------
type Toast = { id: number; message: string; tone: "good" | "danger" | "neutral" };
const ToastContext = createContext<(message: string, tone?: Toast["tone"]) => void>(() => undefined);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const next = useRef(1);
  const push = useCallback((message: string, tone: Toast["tone"] = "good") => {
    const id = next.current++;
    setToasts((items) => [...items, { id, message, tone }]);
    window.setTimeout(() => setToasts((items) => items.filter((t) => t.id !== id)), 4500);
  }, []);
  const value = useMemo(() => push, [push]);
  return (
    <ToastContext.Provider value={value}>
      {children}
      <div aria-live="polite" role="status"
        className="pointer-events-none fixed inset-x-0 bottom-20 z-50 flex flex-col items-center gap-2 px-4 lg:bottom-6">
        {toasts.map((toast) => (
          <div key={toast.id} className={cn("pointer-events-auto rounded-xl px-4 py-2.5 text-[14px] font-medium shadow-pop",
            toast.tone === "danger" ? "bg-danger text-white" : "bg-ink text-bg")}>
            {toast.message}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export const useToast = () => useContext(ToastContext);

export function StatTile({ label, value, detail, icon }:
  { label: string; value: ReactNode; detail?: ReactNode; icon?: ReactNode }) {
  return (
    <div role="group" aria-label={label} className="rounded-2xl border border-line bg-surface p-5 shadow-card">
      <div className="flex items-center gap-2 text-[13px] font-medium text-ink-3">{icon}{label}</div>
      <div data-stat-value className="mt-2 text-[28px] leading-none font-semibold tracking-tight text-ink">{value}</div>
      {detail && <div className="mt-2 text-[13px] text-ink-2">{detail}</div>}
    </div>
  );
}
